// Shared helpers for chat rendering across Chat.jsx and ChatWidget.jsx.
// See doc/ChatIngestion.md for the canonical MSG/SESSION schema.

const ACTION_BLOCK_RE = /```action\s*\n[\s\S]*?```\s*/g;

/** Remove ```action blocks from assistant content for human-facing rendering. */
export function stripActions(text) {
  return (text || "")
    .replace(ACTION_BLOCK_RE, "")
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
}

/**
 * Pull HTML artifact links out of a persisted assistant message's
 * `actionsResults`. The backend's `generate_html_file` action attaches an
 * `artifacts` array with a ready-to-use `download_url`.
 */
export function extractArtifacts(actionsResults) {
  if (!Array.isArray(actionsResults)) return [];
  const artifacts = [];
  for (const a of actionsResults) {
    if (a?.success && ["generate_html_file", "update_html_file", "get_html_download_link", "adopt_html_file"].includes(a?.action) && Array.isArray(a.artifacts)) {
      for (const art of a.artifacts) {
        if (art?.download_url) artifacts.push(art);
      }
    }
  }
  return artifacts;
}

/** Parse a server timestamp (ISO, UTC, no tz suffix) as UTC then format locally. */
export function parseUtc(ts) {
  if (!ts) return null;
  // ChatService writes datetime.utcnow().isoformat() with no trailing Z.
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(ts);
  return new Date(hasTz ? ts : `${ts}Z`);
}

/** Format a timestamp for the message footer (HH:MM in user's locale). */
export function formatTime(ts) {
  const d = parseUtc(ts);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Format byte sizes for attachment / artifact captions. */
export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Kind / source taxonomy mirroring `doc/ChatIngestion.md`. Renderers can use
 * `getKindLabel` to show a small badge above each non-conversation message.
 */
export const KIND_LABELS = {
  conversation: null, // no badge — default rendering
  voice_transcript: { label: "Voice", icon: "mic" },
  email_arrived: { label: "Email", icon: "mail" },
  routine_result: { label: "Routine", icon: "clock" },
  reminder: { label: "Reminder", icon: "bell" },
  device_alert: { label: "Device", icon: "alert" },
  lupulus_task: { label: "Lupulus", icon: "beer" },
  compressed_summary: { label: "Summary", icon: "stack" },
};

export function getKindLabel(kind) {
  return KIND_LABELS[kind] || null;
}
